import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Sparkle, MagicWand, FileText, Hash, Target, X, 
  CheckSquare, ChartLine, ShieldCheck, Certificate 
} from '@phosphor-icons/react';

export default function AIToolbar({ 
  contentType, 
  title,
  content,
  metadata,
  onTitleSuggestions,
  onContentImprovement,
  onSummaryGeneration,
  onTagsSuggestion,
  onSEOGeneration,
  onGrammarCheck,
  onReadabilityScore,
  onQualityAnalysis
}) {
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handleAIAction = async (action) => {
    try {
      setLoading(true);
      setShowMenu(false);

      const API_URL = process.env.REACT_APP_BACKEND_URL;

      switch (action) {
        case 'titles':
          if (!content && !getContentSnippet()) {
            toast.error('Please write some content first');
            return;
          }
          const titlesRes = await axios.post(
            `${API_URL}/api/ai-assist/title-suggestions`,
            {
              content_type: contentType,
              content_snippet: getContentSnippet(),
              language: 'hindi'
            },
            { withCredentials: true }
          );
          onTitleSuggestions(titlesRes.data.titles);
          toast.success('AI generated 5 title suggestions!');
          break;

        case 'improve':
          if (!content && !getMainContent()) {
            toast.error('Please write content first');
            return;
          }
          const improveRes = await axios.post(
            `${API_URL}/api/ai-assist/improve-content`,
            {
              content_type: contentType,
              content: getMainContent(),
              focus: 'general'
            },
            { withCredentials: true }
          );
          onContentImprovement(improveRes.data.improved_content);
          toast.success('Content improved by AI!');
          break;

        case 'summary':
          if (!content && !getMainContent()) {
            toast.error('Please write content first');
            return;
          }
          const summaryRes = await axios.post(
            `${API_URL}/api/ai-assist/generate-summary`,
            {
              content_type: contentType,
              content: getMainContent(),
              length: 'medium'
            },
            { withCredentials: true }
          );
          onSummaryGeneration(summaryRes.data.summary);
          toast.success('Summary generated!');
          break;

        case 'tags':
          if (!title) {
            toast.error('Please add a title first');
            return;
          }
          const tagsRes = await axios.post(
            `${API_URL}/api/ai-assist/suggest-tags`,
            {
              content_type: contentType,
              title: title,
              content: getContentSnippet(),
              language: 'hindi'
            },
            { withCredentials: true }
          );
          onTagsSuggestion(tagsRes.data.tags);
          toast.success(`${tagsRes.data.tags.length} tags suggested!`);
          break;

        case 'seo':
          if (!title || !getMainContent()) {
            toast.error('Please add title and content first');
            return;
          }
          const seoRes = await axios.post(
            `${API_URL}/api/ai-assist/generate-seo`,
            {
              title: title,
              content: getMainContent(),
              content_type: contentType
            },
            { withCredentials: true }
          );
          onSEOGeneration(seoRes.data);
          toast.success('SEO metadata generated!');
          break;

        case 'grammar':
          if (!getMainContent()) {
            toast.error('Please write content first');
            return;
          }
          const grammarRes = await axios.post(
            `${API_URL}/api/ai-assist/grammar-check`,
            {
              content: getMainContent(),
              language: 'hindi'
            },
            { withCredentials: true }
          );
          onGrammarCheck(grammarRes.data);
          toast.success(`Grammar Check: ${grammarRes.data.score}/100 (${grammarRes.data.error_count} issues found)`);
          break;

        case 'readability':
          if (!getMainContent()) {
            toast.error('Please write content first');
            return;
          }
          const readabilityRes = await axios.post(
            `${API_URL}/api/ai-assist/readability-score`,
            {
              content: getMainContent(),
              content_type: contentType
            },
            { withCredentials: true }
          );
          onReadabilityScore(readabilityRes.data);
          toast.success(`Readability: ${readabilityRes.data.readability_score}/100 (${readabilityRes.data.reading_level})`);
          break;

        case 'quality':
          if (!title || !getMainContent()) {
            toast.error('Please add title and content first');
            return;
          }
          const qualityRes = await axios.post(
            `${API_URL}/api/ai-assist/content-quality`,
            {
              content_type: contentType,
              title: title,
              content: getMainContent(),
              metadata: metadata
            },
            { withCredentials: true }
          );
          onQualityAnalysis(qualityRes.data);
          toast.success(`Quality Score: ${qualityRes.data.overall_score}/100 (Grade: ${qualityRes.data.grade})`);
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('AI action error:', error);
      if (error.response?.status === 402) {
        toast.error(error.response.data.detail || 'Emergent LLM Key budget low');
      } else {
        toast.error(error.response?.data?.detail || 'AI service failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const getContentSnippet = () => {
    // Get first 500 chars of main content based on type
    if (metadata?.stanzas && metadata.stanzas.length > 0) {
      return metadata.stanzas.join('\n\n').substring(0, 500);
    }
    if (metadata?.couplets && metadata.couplets.length > 0) {
      return metadata.couplets.join('\n\n').substring(0, 500);
    }
    if (metadata?.shers && metadata.shers.length > 0) {
      return metadata.shers.join('\n\n').substring(0, 500);
    }
    if (metadata?.quote_text) {
      return metadata.quote_text.substring(0, 500);
    }
    if (metadata?.doha_text) {
      return metadata.doha_text.substring(0, 500);
    }
    if (metadata?.story_content) {
      return stripHtml(metadata.story_content).substring(0, 500);
    }
    if (metadata?.article_content) {
      return stripHtml(metadata.article_content).substring(0, 500);
    }
    if (metadata?.essay_content) {
      return stripHtml(metadata.essay_content).substring(0, 500);
    }
    return content || '';
  };

  const getMainContent = () => {
    // Get full main content based on type
    if (metadata?.stanzas && metadata.stanzas.length > 0) {
      return metadata.stanzas.join('\n\n');
    }
    if (metadata?.couplets && metadata.couplets.length > 0) {
      return metadata.couplets.join('\n\n');
    }
    if (metadata?.shers && metadata.shers.length > 0) {
      return metadata.shers.join('\n\n');
    }
    if (metadata?.quote_text) {
      return metadata.quote_text;
    }
    if (metadata?.doha_text) {
      return metadata.doha_text;
    }
    if (metadata?.story_content) {
      return stripHtml(metadata.story_content);
    }
    if (metadata?.article_content) {
      return stripHtml(metadata.article_content);
    }
    if (metadata?.essay_content) {
      return stripHtml(metadata.essay_content);
    }
    return content || '';
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 shadow-lg"
      >
        <Sparkle size={18} weight="fill" className={loading ? 'animate-spin' : ''} />
        <span className="font-medium">AI Assist</span>
      </button>

      {showMenu && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50">
          <div className="p-2 border-b border-border bg-muted/50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">AI Tools</span>
              <button
                onClick={() => setShowMenu(false)}
                className="p-1 hover:bg-muted rounded"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="p-2 space-y-1">
            <button
              onClick={() => handleAIAction('titles')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <MagicWand size={16} className="text-purple-500" />
              <div>
                <div className="text-sm font-medium">Title Suggestions</div>
                <div className="text-xs text-muted-foreground">Generate 5 creative titles</div>
              </div>
            </button>

            <button
              onClick={() => handleAIAction('improve')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <Sparkle size={16} className="text-blue-500" />
              <div>
                <div className="text-sm font-medium">Improve Content</div>
                <div className="text-xs text-muted-foreground">Enhance quality & style</div>
              </div>
            </button>

            <button
              onClick={() => handleAIAction('summary')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <FileText size={16} className="text-green-500" />
              <div>
                <div className="text-sm font-medium">Generate Summary</div>
                <div className="text-xs text-muted-foreground">Auto-create summary</div>
              </div>
            </button>

            <button
              onClick={() => handleAIAction('tags')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <Hash size={16} className="text-orange-500" />
              <div>
                <div className="text-sm font-medium">Suggest Tags</div>
                <div className="text-xs text-muted-foreground">Smart tag generation</div>
              </div>
            </button>

            <button
              onClick={() => handleAIAction('seo')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <Target size={16} className="text-red-500" />
              <div>
                <div className="text-sm font-medium">Generate SEO</div>
                <div className="text-xs text-muted-foreground">Meta title & description</div>
              </div>
            </button>

            <div className="border-t border-border my-1"></div>

            <button
              onClick={() => handleAIAction('grammar')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <CheckSquare size={16} className="text-indigo-500" />
              <div>
                <div className="text-sm font-medium">Grammar Check</div>
                <div className="text-xs text-muted-foreground">Find & fix errors</div>
              </div>
            </button>

            <button
              onClick={() => handleAIAction('readability')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <ChartLine size={16} className="text-cyan-500" />
              <div>
                <div className="text-sm font-medium">Readability Score</div>
                <div className="text-xs text-muted-foreground">Analyze reading level</div>
              </div>
            </button>

            <button
              onClick={() => handleAIAction('quality')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <Certificate size={16} className="text-amber-500" />
              <div>
                <div className="text-sm font-medium">Quality Analysis</div>
                <div className="text-xs text-muted-foreground">Comprehensive review</div>
              </div>
            </button>
          </div>

          <div className="p-2 border-t border-border bg-muted/50">
            <p className="text-xs text-muted-foreground text-center">
              Powered by GPT-5.4
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
